import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { changeOrderApi } from '../../api/endpoints.ts';
import { visitPricingApi } from '../../api/visit-pricing.ts';
import { Alert, Button } from '../ui.tsx';
import {
  ExtraWorkActions,
  ExtraWorkDoneNotice,
  ExtraWorkForm,
  ExtraWorkOrderState,
  type ExtraWorkDone,
} from '../extra-work-form.tsx';
import { useSession } from '../../state/session.tsx';
import { getDateLocale } from '../../i18n/locale.ts';
import { formatCents } from '@shared/calculations/pricing.ts';
import { DEFAULT_CURRENCY_CODE } from '@shared/currency.ts';
import { OPEN_APPOINTMENT_STATUSES } from '@shared/constants/index.ts';
import type { Appointment } from '@shared/types/index.ts';

/**
 * "Extra work" on the visit panel: what the customer asked for beyond the
 * usual on this visit, and the button to add more.
 *
 * Here, rather than only on the Extra work page, because this is where the
 * question comes up — the office has the visit open when the customer texts
 * "can you do the fridge too?" — and the visit is already chosen. The same
 * form the page uses (components/extra-work-form.tsx).
 *
 * `?appointment=<id>&extraWork=1` opens the form straight away for that
 * visit, which is how a customer's upcoming visits link here.
 */
export function VisitExtraWorkCard({ appointment, onChanged }: { appointment: Appointment; onChanged: () => void }) {
  const { t } = useTranslation();
  const { can, company, currentLocation, currencyCode: sessionCurrency } = useSession();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [adding, setAdding] = useState(
    () => params.get('extraWork') === '1' && params.get('appointment') === appointment.id,
  );
  const [done, setDone] = useState<ExtraWorkDone | null>(null);

  const listKey = ['change-orders', 'visit', appointment.id];
  const orders = useQuery({
    queryKey: listKey,
    queryFn: () => changeOrderApi.list({ appointmentId: appointment.id }),
  });
  /*
    Whether this visit can take extra work at all — already paid, priced from
    a range, no price yet — from the same read (and cache) the price card
    makes, so the button is not offered only to be refused. Only for people
    who may read prices; anyone else is told by the server if it cannot.
  */
  const pricing = useQuery({
    queryKey: ['visit-pricing', company?.id, currentLocation?.id, appointment.id],
    queryFn: ({ signal }) => visitPricingApi.get(appointment.id, signal),
    enabled: can('payments:read'),
    retry: false,
  });
  const blockedReason = pricing.data && !pricing.data.editable ? (pricing.data.blockedReason ?? 'closed') : null;
  const open = OPEN_APPOINTMENT_STATUSES.includes(appointment.status);

  const close = () => {
    setAdding(false);
    if (params.has('extraWork')) {
      const next = new URLSearchParams(params);
      next.delete('extraWork');
      setParams(next, { replace: true });
    }
  };
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['change-orders'] });
    void queryClient.invalidateQueries({ queryKey: ['visit-pricing'] });
    void queryClient.invalidateQueries({ queryKey: ['visit-checklist', appointment.id] });
    onChanged();
  };

  const items = orders.data?.items ?? [];
  /*
    More only once what is already on the visit is known: with the list
    missing (still loading, or failed) the office would add the fridge again
    without seeing that it is already there.
  */
  // Likewise the visit's price, for whoever can read it: until it is known
  // (or when it failed) a paid or unpriced visit would look able to take it.
  const pricingKnown = !can('payments:read') || pricing.isSuccess;
  const canAdd = can('quotes:write') && open && !blockedReason && orders.isSuccess && pricingKnown;
  return (
    <section className="card mb-3" aria-label={t('changeOrders.visitCard.title')}>
      <h3 className="font-semibold">{t('changeOrders.visitCard.title')}</h3>
      <p className="mt-1 text-sm text-[color:var(--color-muted)]">{t('changeOrders.visitCard.scope')}</p>

      {done ? (
        <div className="mt-3">
          <ExtraWorkDoneNotice done={done} />
        </div>
      ) : null}

      {orders.isError ? (
        <div className="mt-3">
          <Alert tone="error" title={t('changeOrders.loadErrorTitle')}>
            {(orders.error as Error).message}
          </Alert>
        </div>
      ) : null}

      {items.length ? (
        <ul className="mt-2 divide-y divide-[color:var(--color-line)]">
          {items.map((order) => (
            <li key={order.id} className="py-2">
              <p className="font-medium">
                {order.title} · {formatCents(order.amountCents, order.currencyCode, getDateLocale())}
                <span className="ml-2 badge border-[color:var(--color-line)] text-[color:var(--color-muted)]">
                  {t(`changeOrders.status.${order.status}`)}
                </span>
              </p>
              <ExtraWorkOrderState order={order} />
              <div className="mt-2">
                <ExtraWorkActions
                  order={order}
                  onChanged={refresh}
                  onSent={(result) =>
                    setDone({
                      order: result.changeOrder,
                      link: { url: result.approvalUrl, emailedTo: result.emailedTo },
                    })
                  }
                  onAgreed={(agreed) => setDone({ order: agreed, link: null })}
                />
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {open && blockedReason ? (
        <p className="mt-2 text-sm text-[color:var(--color-muted)]">
          {t('changeOrders.visitCard.cannotTake', { reason: t(`changeOrders.visitBlocked.${blockedReason}`) })}
        </p>
      ) : null}

      {adding && canAdd ? (
        <div className="mt-3">
          <ExtraWorkForm
            customerId={appointment.customerId}
            appointmentId={appointment.id}
            currencyCode={appointment.priceCurrencyCode ?? sessionCurrency}
            onCancel={close}
            onDone={(result) => {
              close();
              setDone(result);
              refresh();
            }}
          />
        </div>
      ) : canAdd ? (
        <Button
          className="mt-3"
          variant="secondary"
          onClick={() => {
            setDone(null);
            setAdding(true);
          }}
        >
          {t('changeOrders.raise')}
        </Button>
      ) : null}
    </section>
  );
}
